import type { Sequence } from "../types/Sequence"

/**
 * Reduces a sequence to a single value using an accumulator function.
 * Processes items left-to-right, passing accumulator through each step.
 *
 * @param func - Reducer function (accumulator, item, index) => new accumulator
 * @param sequence - Sequence to reduce
 * @param initial - Initial accumulator value
 * @returns Final accumulated value
 *
 * @example
 * ```ts
 * import { Reduce } from "@pdfking/tfunc"
 *
 * const sum = Reduce((acc, x) => acc + x, [1, 2, 3, 4], 0)
 * // 10
 *
 * const product = Reduce((acc, x) => acc * x, [1, 2, 3, 4], 1)
 * // 24
 *
 * const concat = Reduce((acc, x, i) => `${acc}${i}: ${x}\n`, ["a", "b"], "")
 * // "0: a\n1: b\n"
 * ```
 */
export const Reduce = <T, A>(
  func: (accumulator: A, item: T, index: number) => A,
  sequence: Sequence<T>,
  initial: A
): A => {
  let accumulator = initial
  let index = 0

  for (const item of sequence) {
    accumulator = func(accumulator, item, index++)
  }

  return accumulator
}
